import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Crosshair, Trash2 } from "lucide-react";
import clsx from "clsx";
import { PanelSelect } from "../../../PanelSelect";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { DestinationIcon } from "../DestinationIcon";
import type { DestinationRowProps } from "./types";

export const DestinationRow = ({
  entry,
  showName,
  showLocator,
  currentCircuitId,
  options,
  edit,
  isPinned,
  onPin,
  onHoverIn,
  onHoverOut,
  onHoverCircuit,
}: DestinationRowProps) => {
  const { t } = useTranslation();

  const targets = useMemo(
    () =>
      options
        // Never the run it is already on: "move it to where it is" is no move.
        .filter((option) => option.id !== currentCircuitId)
        .map((option) => ({
          value: option.id,
          label: option.label,
          color: option.color,
        })),
    [currentCircuitId, options],
  );

  const { info, destinationId, number } = entry;
  const canAct = Boolean(destinationId) && Boolean(edit);
  const locator = showLocator ? info.locator?.trim() : undefined;

  return (
    <div
      data-testid={`destination-${destinationId ?? info.name}`}
      onMouseEnter={onHoverIn}
      onMouseLeave={onHoverOut}
      className={clsx(
        "group flex items-center gap-2 rounded border-t border-gray-100 px-1 py-1.5",
        isPinned ? "bg-amber-100" : "hover:bg-gray-50",
      )}
    >
      {/* Clicking it PINS: he needs the device to stay marked while he takes
          the mouse to the drawing to check it is the one he means. */}
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onPin();
        }}
        title={t("smartSurveyor.cablesBeta.stepShowOnPlan")}
        className="flex min-w-0 flex-1 items-center gap-2 text-left"
      >
        <DestinationIcon
          dataUrl={info.iconDataUrl}
          title={info.name}
          size="xs"
        />
        {number !== undefined && (
          <span
            title={t("smartSurveyor.cablesBeta.deviceNumbered", {
              number,
            })}
            className="flex-none text-[13px] font-semibold tabular-nums text-slate-500"
          >
            {`#${number}`}
          </span>
        )}
        {showName && (
          <span
            className="min-w-0 flex-1 truncate text-sm text-gray-800"
            title={info.name}
          >
            {info.name || "—"}
          </span>
        )}
        {locator && (
          <span
            className={clsx(
              "min-w-0 truncate rounded bg-slate-100 px-1.5 py-0.5 text-[13px] text-slate-600",
              showName ? "flex-none max-w-[8rem]" : "flex-1",
            )}
            title={locator}
          >
            {locator}
          </span>
        )}
        {!showName && !locator && <span className="flex-1" />}
        {isPinned && (
          <Crosshair size={14} className="flex-none text-amber-700" />
        )}
      </button>

      {canAct && (
        <span className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            title={t("smartSurveyor.cablesBeta.removeFromRoute")}
            onClick={(event) => {
              event.stopPropagation();
              trackEvent(
                ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
                { action: "removed", deviceCount: 1 },
              );
              edit!.removeDevice(destinationId!);
            }}
            className="grid h-9 w-9 place-items-center rounded-md border border-transparent text-slate-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 size={16} />
          </button>
          {targets.length > 0 && (
            <PanelSelect
              value=""
              options={targets}
              trigger="icon"
              placeholder={t("smartSurveyor.cablesBeta.removedDeviceMoveOnto")}
              ariaLabel={t("smartSurveyor.cablesBeta.moveToAnotherRoute")}
              onActiveChange={onHoverCircuit}
              onPick={(to) => {
                trackEvent(
                  ANALYTICS_EVENTS.SMART_SURVEYOR_CABLE_RUN_DEVICES_CHANGED,
                  { action: "moved", deviceCount: 1 },
                );
                edit!.reassignDevice(destinationId!, to);
              }}
            />
          )}
        </span>
      )}
    </div>
  );
};
